import type { SettingLocation } from "./settings-adoption-view"

const at = (
	href: string,
	order: number,
	label: string,
	section?: string,
): SettingLocation => ({ href, order, label, section })

const AGENT = "/settings/ai/agent"
const PORTAL = "/settings/portal"
const THEME = "/settings/portal/theme"
const SSO = "/settings/portal/sso"
const WIDGET = "/settings/widget"
const ROADMAP = "/settings/roadmap"
const LINEAR = "/settings/integrations/linear"
const SLACK = "/settings/integrations/slack"
const EMAILS = "/settings/emails"

// Where each setting is changed, keyed Model.field. A key missing here renders under "Not in settings".
export const SETTING_LOCATIONS: Record<string, SettingLocation> = {
	"Workspace.aiResolutionMonthlyLimit": at(
		"/settings/ai/usage",
		1,
		"Monthly cap",
		"AI resolutions",
	),
	"Workspace.aiPayPerUseEnabled": at(
		"/settings/ai/usage",
		2,
		"Enable pay-per-use",
		"AI resolutions",
	),
	"Workspace.aiTokenMonthlyCapCents": at(
		"/settings/ai/usage",
		3,
		"Monthly overage limit",
		"AI credit usage",
	),

	"AiChatSettings.isEnabled": at(AGENT, 1, "Productlane Agent"),
	"AiChatSettings.aiAutoRepliesEnabled": at(AGENT, 2, "Email", "Deploy"),
	"AiChatSettings.deploySlack": at(AGENT, 3, "Slack", "Deploy"),
	"AiChatSettings.deployTeams": at(AGENT, 4, "Microsoft Teams", "Deploy"),
	"AiChatSettings.deployApi": at(AGENT, 5, "API", "Deploy"),
	"AiChatSettings.deploySupportPortal": at(
		AGENT,
		6,
		"Support portal",
		"Deploy",
	),
	"AiChatSettings.deployFeatureRequests": at(
		AGENT,
		7,
		"Feature requests",
		"Deploy",
	),
	"AiChatSettings.createLinearIssues": at(
		AGENT,
		8,
		"Create Linear issues from agent chats",
		"Linear",
	),
	"Workspace.autoAssignAgentToBugs": at(
		AGENT,
		9,
		"Auto-assign Productlane Agent to bugs",
		"Linear",
	),
	"Workspace.excludeManuallyCreatedIssues": at(
		AGENT,
		10,
		"Exclude manually created issues",
		"Linear",
	),
	"AiChatSettings.welcomeMessage": at(
		AGENT,
		11,
		"Welcome message",
		"Customize",
	),
	"AiChatSettings.exampleQuestions": at(AGENT, 12, "Suggestions", "Customize"),
	"AiChatSettings.customInstructions": at(
		AGENT,
		13,
		"Workspace guidance",
		"Workspace guidance",
	),

	"AiChatSettings.closeLoopAutoSend": at(
		"/settings/ai/close-loop",
		1,
		"Send follow-ups automatically",
	),
	"AiChatSettings.closeLoopIssueStatusIds": at(
		"/settings/ai/close-loop",
		2,
		"Released issue statuses",
	),
	"AiChatSettings.closeLoopProjectStatusIds": at(
		"/settings/ai/close-loop",
		3,
		"Released project statuses",
	),
	"AiChatSettings.closeLoopTemplate": at(
		"/settings/ai/close-loop",
		4,
		"Message for a reported issue",
	),
	"AiChatSettings.closeLoopProjectTemplate": at(
		"/settings/ai/close-loop",
		5,
		"Message for a released project",
	),
	"AiChatSettings.closeLoopUpvoteTemplate": at(
		"/settings/ai/close-loop",
		6,
		"Message for an upvote",
	),

	"AiChatSettings.routinesEnabled": at(
		"/settings/ai/routines",
		1,
		"Run a routine on every new thread",
	),
	"AiChatSettings.routinesPrompt": at(
		"/settings/ai/routines",
		2,
		"Instructions",
	),

	"AiChatSettings.aiDraftsEnabled": at(
		"/settings/ai/drafts",
		1,
		"Generate drafts with AI",
	),
	"AiChatSettings.aiDraftsCustomPrompt": at(
		"/settings/ai/drafts",
		2,
		"Custom prompt",
	),

	"Workspace.helpCenterPublic": at("/settings/docs", 1, "Make Docs public"),
	"Workspace.autoProposeDocsUpdates": at(
		"/settings/docs",
		2,
		"Self-updating help center",
	),
	"Workspace.docsCustomPrompt": at("/settings/docs", 3, "Custom instructions"),
	"Workspace.helpCenterShowAiActions": at(
		"/settings/docs",
		4,
		"AI actions on docs",
	),

	"WidgetSettings.isPortalAuthEnabled": at(
		WIDGET,
		1,
		"Portal authentication",
		"Authenticate",
	),
	"WidgetSettings.identityVerificationEnabled": at(
		WIDGET,
		2,
		"Signing secret generated",
		"Authenticate",
	),
	"WidgetSettings.isLiveChatEnabled": at(
		WIDGET,
		3,
		"Contact options: Live chat",
		"Customize",
	),
	"WidgetSettings.isFeedbackEnabled": at(
		WIDGET,
		4,
		"Contact options: Contact form",
		"Customize",
	),
	"WidgetSettings.isGuestChatEnabled": at(
		WIDGET,
		5,
		"Who can start a conversation",
		"Customize",
	),
	"WidgetSettings.isAiFirstEnabled": at(
		WIDGET,
		6,
		"Where visitors start",
		"Customize",
	),
	"WidgetSettings.isRoadmapEnabled": at(
		WIDGET,
		7,
		"Feature requests",
		"Customize",
	),
	"WidgetSettings.isChangelogEnabled": at(WIDGET, 8, "Changelog", "Customize"),
	"WidgetSettings.isWhatsNewEnabled": at(
		WIDGET,
		9,
		"Changelog: what's new",
		"Customize",
	),
	"WidgetSettings.isSupportEnabled": at(
		WIDGET,
		10,
		"Support Portal",
		"Customize",
	),
	"WidgetSettings.isHelpCenterEnabled": at(WIDGET, 11, "Docs", "Customize"),
	"WidgetSettings.widgetSize": at(WIDGET, 12, "Widget size", "Customize"),
	"WidgetSettings.widgetIconType": at(WIDGET, 13, "Widget icon", "Customize"),
	"WidgetSettings.isWidgetIconVisible": at(
		WIDGET,
		14,
		"Widget icon: visible",
		"Customize",
	),
	"WidgetSettings.hasCustomLinksEnabled": at(
		WIDGET,
		15,
		"Custom links",
		"Customize",
	),
	"WidgetSettings.isContactAvatarsEnabled": at(
		WIDGET,
		16,
		"Contact avatars",
		"Customize",
	),
	"WidgetSettings.isBrandingEnabled": at(
		WIDGET,
		17,
		"Productlane branding",
		"Customize",
	),

	"Workspace.isGoogleAnalyticsEnabled": at(PORTAL, 1, "Google Analytics"),
	"Workspace.googleTagId": at(PORTAL, 2, "Google tag ID"),
	"Workspace.hideBranding": at(PORTAL, 3, "Branding"),
	"Workspace.isGoogleAuthRequired": at(
		PORTAL,
		4,
		"Require sign-in",
		"Main portal: Authentication",
	),
	"Workspace.googleDomain": at(
		PORTAL,
		5,
		"Who can sign in",
		"Main portal: Authentication",
	),
	"Workspace.usePortalLogoUrl": at(
		PORTAL,
		6,
		"Custom home link",
		"Main portal: Custom links",
	),
	"Workspace.portalLogoUrl": at(
		PORTAL,
		7,
		"Home link URL",
		"Main portal: Custom links",
	),
	"Workspace.changelogLink1Description": at(
		PORTAL,
		8,
		"Footer link 1 title",
		"Main portal: Custom links",
	),
	"Workspace.changelogLink1Url": at(
		PORTAL,
		9,
		"Footer link 1 URL",
		"Main portal: Custom links",
	),
	"Workspace.changelogLink2Description": at(
		PORTAL,
		10,
		"Footer link 2 title",
		"Main portal: Custom links",
	),
	"Workspace.changelogLink2Url": at(
		PORTAL,
		11,
		"Footer link 2 URL",
		"Main portal: Custom links",
	),

	"Workspace.portalEmbedImageUrl": at(THEME, 1, "Embed image"),
	"Workspace.darkMode": at(THEME, 2, "Dark mode"),
	"Workspace.accentColor": at(THEME, 3, "Accent color", "Light mode"),
	"Workspace.buttonTextColor": at(THEME, 4, "Button text color", "Light mode"),
	"Workspace.darkModeAccentColor": at(THEME, 5, "Accent color", "Dark mode"),
	"Workspace.darkModeButtonTextColor": at(
		THEME,
		6,
		"Button text color",
		"Dark mode",
	),

	"Workspace.customDomain": at("/settings/portal/domain", 1, "Your domain"),

	"SsoSettings.isEnabled": at(SSO, 1, "Enable SSO"),
	"SsoSettings.isPortalPrivate": at(SSO, 2, "Make entire portal private"),
	"SsoSettings.isDocsPrivate": at(SSO, 3, "Make Docs private"),
	"SsoSettings.isRoadmapPrivate": at(SSO, 4, "Make Feature requests private"),
	"SsoSettings.isChangelogPrivate": at(SSO, 5, "Make Changelog private"),
	"SsoSettings.isRequestsPrivate": at(SSO, 6, "Make Support Portal private"),

	"EmailSettings.autoReplyEnabled": at(
		EMAILS,
		1,
		"Auto-reply",
		"Address: Automation",
	),
	"EmailSettings.autoReplySenderName": at(
		EMAILS,
		2,
		"Sender name",
		"Address: Automation",
	),
	"EmailSettings.autoReplyMessage": at(
		EMAILS,
		3,
		"Message",
		"Address: Automation",
	),
	"EmailSettings.forwardedEmailHandling": at(
		"/settings/emails/forwarding",
		1,
		"Forwarded email sender",
	),

	"Workspace.domainPublic": at(ROADMAP, 1, "Make Feature requests public"),
	"RoadmapSettings.sortOrder": at(ROADMAP, 2, "Sorting"),
	"RoadmapSettings.groupIssuesUnderProject": at(ROADMAP, 3, "Issue grouping"),
	"Workspace.isDueDateEnabled": at(ROADMAP, 4, "Due dates"),
	"Workspace.isRoadmapsEnabled": at(ROADMAP, 5, "Initiatives"),
	"RoadmapSettings.linearIssueDescription": at(
		ROADMAP,
		6,
		"Linear issue description",
	),
	"Workspace.weeklyRoadmapEmailEnabled": at(ROADMAP, 7, "Weekly roadmap email"),

	"Workspace.changelogPublic": at(
		"/settings/changelog",
		1,
		"Make Changelog public",
	),
	"Workspace.subscribeUrl": at("/settings/changelog", 2, "Email subscription"),
	"Workspace.changelogAutomaticDrafts": at(
		"/settings/changelog",
		3,
		"Release intelligence",
	),
	"Workspace.changelogCustomPrompt": at(
		"/settings/changelog",
		4,
		"Custom prompt",
	),

	"CustomerPortalSettings.isEnabled": at(
		"/settings/support-portal",
		1,
		"Make Support Portal public",
	),
	"CustomerPortalSettings.teamRequests": at(
		"/settings/support-portal",
		2,
		"Team requests",
	),
	"CustomerPortalSettings.portalCompanyScope": at(
		"/settings/support-portal",
		3,
		"Cross-company thread visibility",
	),

	"Workspace.maxResponseTimeHours": at(
		"/settings/office-hours",
		1,
		"Response time",
	),
	"Workspace.timezone": at("/settings/office-hours", 2, "Timezone"),
	"Workspace.officeHours": at("/settings/office-hours", 3, "Office hours"),
	"Workspace.slaEnabled": at(
		"/settings/office-hours",
		4,
		"Enable SLAs",
		"SLAs",
	),
	"Workspace.slaUseBusinessHours": at(
		"/settings/office-hours",
		5,
		"Use office hours",
		"SLAs",
	),

	"Workspace.defaultLanguage": at("/settings/languages", 1, "Default language"),
	"Workspace.enabledLanguages": at(
		"/settings/languages",
		2,
		"Enabled languages",
	),

	"Workspace.autoAssignFromLinearOwner": at(
		"/settings/assignments",
		1,
		"Auto-assign to Linear owner",
	),
	"Workspace.autoSwitchContactToLatestSender": at(
		"/settings/assignments",
		2,
		"Follow latest email sender",
	),
	"Workspace.domainCompanyAssignmentEnabled": at(
		"/settings/assignments",
		3,
		"Auto-assign contacts to companies by domain",
	),

	"Workspace.autoCreateLinearIssues": at(
		LINEAR,
		1,
		"Auto-create issues",
		"Automation",
	),
	"Workspace.autoCreateLinearCustomers": at(
		LINEAR,
		2,
		"Auto-create customers",
		"Automation",
	),
	"Workspace.postLinearResourceComment": at(
		LINEAR,
		3,
		"Reply to customers from Linear",
		"Automation",
	),
	"Workspace.linkSuggestionsEnabled": at(
		LINEAR,
		4,
		"Suggest issues and tags",
		"Automation",
	),
	"Workspace.revenueFormatting": at(
		LINEAR,
		5,
		"Revenue formatting",
		"Display options",
	),
	"Workspace.revenueCurrency": at(
		LINEAR,
		6,
		"Revenue currency",
		"Display options",
	),

	"SlackConnectSettings.type": at(
		SLACK,
		1,
		"Message grouping: mode",
		"Options",
	),
	"SlackConnectSettings.groupingWindowMinutes": at(
		SLACK,
		2,
		"Message grouping: window",
		"Options",
	),
	"SlackConnectSettings.notificationChannelId": at(
		SLACK,
		3,
		"Notifications",
		"Options",
	),
	// Muted channels became the Inbox setting on each channel; this is the default for new ones.
	"SlackConnectSettings.newChannelsInbox": at(
		SLACK,
		4,
		"New channels",
		"Workspaces",
	),
	"SlackConnectSettings.pushBotReplyChannel": at(
		SLACK,
		5,
		"Thread auto-replies",
		"Action menu",
	),
	"SlackConnectSettings.channelWelcomeEnabled": at(
		SLACK,
		6,
		"Channel welcome",
		"Welcome messages",
	),
	"SlackConnectSettings.channelWelcomeMessage": at(
		SLACK,
		7,
		"Channel welcome: message",
		"Welcome messages",
	),
	"SlackConnectSettings.communityWelcomeEnabled": at(
		SLACK,
		8,
		"Community welcome",
		"Welcome messages",
	),
	"SlackConnectSettings.communityWelcomeMessage": at(
		SLACK,
		9,
		"Community welcome: message",
		"Welcome messages",
	),

	"PostgresSettings.autoRunQueries": at(
		"/settings/integrations/postgres",
		1,
		"Auto-run queries the assistant generates",
	),
	"IntercomSettings.dataHostRegion": at(
		"/settings/integrations/intercom",
		1,
		"Data host region",
		"Options",
	),

	"CursorSettings.repository": at(
		"/settings/integrations/cursor",
		1,
		"Repository",
		"Configuration",
	),
	"CursorSettings.model": at(
		"/settings/integrations/cursor",
		2,
		"Model",
		"Configuration",
	),
	"CursorSettings.recordingMode": at(
		"/settings/integrations/cursor",
		3,
		"Recording",
		"Configuration",
	),
}
